import type { useLoaderData } from 'react-router';
import { FACET_SEPARATOR } from '~/helpers/algolia';
import type { CatalogLoader } from './route';

type LoaderData = ReturnType<typeof useLoaderData<CatalogLoader>>;

export function buildFilterState({
  slugHierarchyFull,
  slugMapping,
  menuFacets,
  menuRefinement,
  baseRoute,
  allLabel,
}: Pick<
  LoaderData,
  'slugHierarchyFull' | 'slugMapping' | 'menuFacets' | 'menuRefinement'
> & { baseRoute: string; allLabel: string }): FilterState {
  const result: FilterState = {
    level0Label: allLabel,
    level1Items: [],
    level2Items: [],
  };

  // ["slugA > slugB > slugC"] => ["slugA", "slugA > slugB", "slugA > slugB > slugC"]
  const [level0Value, level1Value, level2Value] = menuRefinement
    .split(FACET_SEPARATOR)
    .reduce<(string | undefined)[]>((acc, _, i, all) => {
      acc.push(all.slice(0, i + 1).join(FACET_SEPARATOR));

      return acc;
    }, []);

  if (!level0Value) return result;

  result.level0Label = slugMapping?.[level0Value] ?? level0Value;

  const levels = [
    [slugHierarchyFull?.lvl1 ?? [], level1Value, result.level1Items],
    [slugHierarchyFull?.lvl2 ?? [], level2Value, result.level2Items],
  ] as const;

  let rootValue = level0Value;
  let rootFacets = menuFacets?.find(({ path }) => path === rootValue)?.data;

  for (const [levelValues, levelValue, items] of levels) {
    let totalCount = 0;
    let hasCurrent = false;

    for (const value of levelValues) {
      if (!value.startsWith(rootValue)) continue;

      const count = rootFacets?.find(({ path }) => path === value)?.count ?? 0;
      const isCurrent = value === levelValue;

      totalCount += count;
      hasCurrent ||= isCurrent;

      items.push({
        value,
        count,
        isCurrent,
        label: slugMapping?.[value] ?? value,
        pathname: createPathname(baseRoute, value),
        isRoot: false,
      });
    }

    if (!items.length) return result;

    items.unshift({
      value: rootValue,
      label: allLabel,
      pathname: createPathname(baseRoute, rootValue),
      count: totalCount,
      isCurrent: !hasCurrent,
      isRoot: true,
    });

    if (!levelValue) return result;

    rootValue = levelValue;
    rootFacets = rootFacets?.find(({ path }) => path === levelValue)?.data;
  }

  return result;
}

function createPathname(baseRoute: string, value: string) {
  return `${[baseRoute, ...value.split(FACET_SEPARATOR).filter(Boolean)].join('/')}/`;
}

export type FilterState = {
  level0Label: string;
  level1Items: FilterItem[];
  level2Items: FilterItem[];
};

type FilterItem = {
  label: string;
  value: string;
  pathname: string;
  count: number;
  isCurrent: boolean;
  isRoot: boolean;
};
